import { useState, useEffect } from 'react'
import { Table, Card, Button, Space, Modal, Form, Input, Select, Tag, message, Popconfirm } from 'antd'
import { PlusOutlined, SearchOutlined, ReloadOutlined } from '@ant-design/icons'
import { pickupPointApi } from '../services/api'

const { Option } = Select

interface PickupPoint {
  id: number
  name: string
  address: string
  district_code: string
  district_name: string
  city_name: string
  province_name: string
  contact_name: string
  contact_phone: string
  pickup_start_time: string
  pickup_end_time: string
  longitude: number
  latitude: number
  sort_order: number
  status: number
  remark: string
  created_at: string
}

// 上海区县列表
const SHANGHAI_DISTRICTS = [
  { code: '310101', name: '黄浦区' },
  { code: '310104', name: '徐汇区' },
  { code: '310105', name: '长宁区' },
  { code: '310106', name: '静安区' },
  { code: '310107', name: '普陀区' },
  { code: '310109', name: '虹口区' },
  { code: '310110', name: '杨浦区' },
  { code: '310112', name: '闵行区' },
  { code: '310113', name: '宝山区' },
  { code: '310114', name: '嘉定区' },
  { code: '310115', name: '浦东新区' },
  { code: '310116', name: '金山区' },
  { code: '310117', name: '松江区' },
  { code: '310118', name: '青浦区' },
  { code: '310120', name: '奉贤区' },
  { code: '310151', name: '崇明区' },
]

export default function PickupPoints() {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<PickupPoint[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [modalVisible, setModalVisible] = useState(false)
  const [editMode, setEditMode] = useState(false)
  const [currentPoint, setCurrentPoint] = useState<PickupPoint | null>(null)
  const [form] = Form.useForm()
  const [keyword, setKeyword] = useState('')

  useEffect(() => {
    loadData()
  }, [page, pageSize])

  const loadData = async () => {
    setLoading(true)
    try {
      const res = await pickupPointApi.getAdminList({ page, page_size: pageSize, keyword })
      if (res.code === 200) {
        setData(res.data?.list || [])
        setTotal(res.data?.total || 0)
      }
    } catch (err) {
      message.error('加载自提点列表失败')
    } finally {
      setLoading(false)
    }
  }

  const handleAdd = () => {
    setEditMode(false)
    setCurrentPoint(null)
    form.resetFields()
    setModalVisible(true)
  }

  const handleEdit = (record: PickupPoint) => {
    setEditMode(true)
    setCurrentPoint(record)
    form.setFieldsValue({
      name: record.name,
      address: record.address,
      district_code: record.district_code,
      contact_name: record.contact_name || '',
      contact_phone: record.contact_phone || '',
      pickup_start_time: record.pickup_start_time || '',
      pickup_end_time: record.pickup_end_time || '',
      sort_order: record.sort_order || 0,
      status: record.status,
      remark: record.remark || ''
    })
    setModalVisible(true)
  }

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()

      // 查找区县名称
      const district = SHANGHAI_DISTRICTS.find(d => d.code === values.district_code)
      const payload = {
        ...values,
        district_name: district?.name || '',
        city_name: '上海市',
        province_name: '上海市'
      }

      let res
      if (editMode && currentPoint) {
        res = await pickupPointApi.update(currentPoint.id, payload)
      } else {
        res = await pickupPointApi.create(payload)
      }

      if (res.code === 200) {
        message.success(editMode ? '更新成功' : '新增成功')
        setModalVisible(false)
        loadData()
      } else {
        message.error(res.message || '操作失败')
      }
    } catch (err) {
      console.error(err)
    }
  }

  const handleDelete = async (id: number) => {
    try {
      const res = await pickupPointApi.delete(id)
      if (res.code === 200) {
        message.success('删除成功')
        loadData()
      } else {
        message.error(res.message || '删除失败')
      }
    } catch (err) {
      message.error('删除失败')
    }
  }

  const handleToggleStatus = async (record: PickupPoint) => {
    try {
      const res = await pickupPointApi.toggleStatus(record.id)
      if (res.code === 200) {
        message.success('状态已更新')
        loadData()
      }
    } catch (err) {
      message.error('操作失败')
    }
  }

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: '自提点名称', dataIndex: 'name', key: 'name', width: 150 },
    { title: '地址', dataIndex: 'address', key: 'address', ellipsis: true },
    {
      title: '所在区县',
      key: 'district',
      width: 100,
      render: (_: any, record: PickupPoint) => (
        <Tag color="blue">{record.district_name || '-'}</Tag>
      )
    },
    {
      title: '提货时间',
      key: 'pickup_time',
      width: 140,
      render: (_: any, record: PickupPoint) => {
        if (record.pickup_start_time && record.pickup_end_time) {
          return `${record.pickup_start_time} - ${record.pickup_end_time}`
        }
        return '-'
      }
    },
    {
      title: '排序',
      dataIndex: 'sort_order',
      key: 'sort_order',
      width: 60
    },
    {
      title: '状态',
      key: 'status',
      width: 80,
      render: (_: any, record: PickupPoint) => (
        <Tag color={record.status === 1 ? 'green' : 'red'}>
          {record.status === 1 ? '启用' : '禁用'}
        </Tag>
      )
    },
    {
      title: '操作',
      key: 'action',
      width: 180,
      render: (_: any, record: PickupPoint) => (
        <Space size="small">
          <Button type="link" size="small" onClick={() => handleEdit(record)}>编辑</Button>
          <Button type="link" size="small" onClick={() => handleToggleStatus(record)}>
            {record.status === 1 ? '禁用' : '启用'}
          </Button>
          <Popconfirm title="确认删除该自提点?" onConfirm={() => handleDelete(record.id)}>
            <Button type="link" size="small" danger>删除</Button>
          </Popconfirm>
        </Space>
      )
    }
  ]

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <Space>
            <Input
              placeholder="搜索名称/地址"
              value={keyword}
              onChange={e => setKeyword(e.target.value)}
              onPressEnter={() => { setPage(1); loadData() }}
              style={{ width: 200 }}
              prefix={<SearchOutlined />}
            />
            <Button icon={<ReloadOutlined />} onClick={() => { setKeyword(''); setPage(1); loadData() }}>重置</Button>
          </Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>新增自提点</Button>
        </div>

        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showTotal: (t) => `共 ${t} 条`,
            onChange: (p, ps) => { setPage(p); setPageSize(ps) }
          }}
        />
      </Card>

      {/* 新增/编辑弹窗 */}
      <Modal
        title={editMode ? '编辑自提点' : '新增自提点'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={600}
        destroyOnClose
      >
        <Form form={form} labelCol={{ span: 6 }} wrapperCol={{ span: 16 }}>
          <Form.Item name="name" label="自提点名称" rules={[{ required: true, message: '请输入自提点名称' }]}>
            <Input placeholder="如：XX社区自提点" />
          </Form.Item>
          <Form.Item name="address" label="详细地址" rules={[{ required: true, message: '请输入详细地址' }]}>
            <Input placeholder="详细地址" />
          </Form.Item>
          <Form.Item name="district_code" label="所在区县" rules={[{ required: true, message: '请选择区县' }]}>
            <Select placeholder="请选择区县" showSearch filterOption={(input, option) =>
              (option?.children as unknown as string)?.includes(input)
            }>
              {SHANGHAI_DISTRICTS.map(d => (
                <Option key={d.code} value={d.code}>{d.name}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="提货时间" required>
            <Space>
              <Form.Item name="pickup_start_time" noStyle rules={[{ required: true, message: '开始时间' }]}>
                <Input placeholder="08:00" style={{ width: 100 }} />
              </Form.Item>
              <span>至</span>
              <Form.Item name="pickup_end_time" noStyle rules={[{ required: true, message: '结束时间' }]}>
                <Input placeholder="18:00" style={{ width: 100 }} />
              </Form.Item>
            </Space>
          </Form.Item>
          <Form.Item name="contact_name" label="联系人">
            <Input placeholder="联系人（可选）" />
          </Form.Item>
          <Form.Item name="contact_phone" label="联系电话">
            <Input placeholder="联系电话（可选）" />
          </Form.Item>
          <Form.Item name="sort_order" label="排序">
            <Input type="number" placeholder="0" />
          </Form.Item>
          <Form.Item name="status" label="状态" initialValue={1}>
            <Select>
              <Option value={1}>启用</Option>
              <Option value={0}>禁用</Option>
            </Select>
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={2} placeholder="备注信息" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
